/**
 * Lab member-betlist `ui-select` — Enter Member Name.
 * Live: CM-API `POST /v1/member/searchHierarchyNew` (100ms debounce).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Field, Icon, Input, Spinner } from '@w7/ui'
import { useSearchHierarchyNew } from '@/api/hooks/member/useSearchHierarchyNew'
import './bet-list-member-picker.css'

const DEBOUNCE_MS = 100

export type BetListMemberNamePickerProps = {
  value: string | null
  onChange: (username: string | null) => void
  /** Kept for Agent-UI prop parity. */
  rootId: string
}

export function BetListMemberNamePicker({
  value,
  onChange,
}: BetListMemberNamePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const prevValueRef = useRef(value)
  const [draft, setDraft] = useState(value ?? '')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (value != null) {
      setDraft(value)
    } else if (!open && prevValueRef.current != null) {
      setDraft('')
    }
    prevValueRef.current = value
  }, [value, open])

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const hierarchy = useSearchHierarchyNew(debounced, open, { prefetchOnEmpty: true })
  const suggestions = useMemo(
    () => (hierarchy.data ?? []).slice(0, 20),
    [hierarchy.data],
  )
  const fetching = hierarchy.isFetching

  const pick = (name: string) => {
    onChange(name)
    setDraft(name)
    setOpen(false)
  }

  const clear = () => {
    setDraft('')
    onChange(null)
    setOpen(false)
  }

  const showClear = Boolean(draft.trim() || value)

  return (
    <div className="w7-betlist-member-picker" ref={rootRef}>
      <Field label="Enter Member Name">
        {({ id }) => (
          <div className="w7-betlist-member-wrap">
            <Input
              id={id}
              aria-label="Enter Member Name"
              placeholder="Select Names to Search"
              value={draft}
              className={showClear ? 'w7-betlist-member-input--clearable' : undefined}
              aria-expanded={open}
              aria-controls="w7-betlist-member-suggest"
              aria-autocomplete="list"
              aria-busy={fetching}
              onChange={(e) => {
                setDraft(e.target.value)
                onChange(null)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
            />
            {showClear ? (
              <button
                type="button"
                className="w7-betlist-member-clear"
                aria-label="Clear member name"
                onMouseDown={(e) => e.preventDefault()}
                onClick={clear}
              >
                <Icon name="close" size={14} />
              </button>
            ) : null}
            {open ? (
              <ul
                id="w7-betlist-member-suggest"
                className="w7-betlist-member-suggest"
                role="listbox"
              >
                {fetching && suggestions.length === 0 ? (
                  <li className="w7-betlist-member-suggest-empty" role="presentation">
                    <Spinner size={14} /> Searching…
                  </li>
                ) : null}
                {suggestions.length === 0 && !fetching ? (
                  <li className="w7-betlist-member-suggest-empty">No members found</li>
                ) : (
                  suggestions.map((name) => (
                    <li key={name}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={value === name}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          pick(name)
                        }}
                      >
                        {name}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        )}
      </Field>
    </div>
  )
}
